/**
 * CNPJ Validation Utility
 * Validates Brazilian CNPJ (Cadastro Nacional de Pessoa Jurídica)
 */

/**
 * Format CNPJ string to XX.XXX.XXX/XXXX-XX
 */
export function formatCNPJ(cnpj: string): string {
  const cleaned = cnpj.replace(/\D/g, '');

  if (cleaned.length !== 14) {
    return cnpj;
  }

  return cleaned.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    '$1.$2.$3/$4-$5'
  );
}

/**
 * Remove CNPJ formatting
 */
export function cleanCNPJ(cnpj: string): string {
  return cnpj.replace(/\D/g, '');
}

/**
 * Validate CNPJ using the official algorithm
 */
export function validateCNPJ(cnpj: string): boolean {
  const cleaned = cleanCNPJ(cnpj);

  // Must have 14 digits
  if (cleaned.length !== 14) {
    return false;
  }

  // Check if all digits are the same (invalid CNPJs like 00000000000000)
  if (/^(\d)\1+$/.test(cleaned)) {
    return false;
  }

  // Validate first check digit
  let sum = 0;
  let weight = 5;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(cleaned[i]) * weight;
    weight = weight === 2 ? 9 : weight - 1;
  }
  let remainder = sum % 11;
  const digit1 = remainder < 2 ? 0 : 11 - remainder;

  if (parseInt(cleaned[12]) !== digit1) {
    return false;
  }

  // Validate second check digit
  sum = 0;
  weight = 6;
  for (let i = 0; i < 13; i++) {
    sum += parseInt(cleaned[i]) * weight;
    weight = weight === 2 ? 9 : weight - 1;
  }
  remainder = sum % 11;
  const digit2 = remainder < 2 ? 0 : 11 - remainder;

  if (parseInt(cleaned[13]) !== digit2) {
    return false;
  }

  return true;
}

/**
 * Get CNPJ validation error message
 */
export function getCNPJError(cnpj: string): string | null {
  const cleaned = cleanCNPJ(cnpj);

  if (!cnpj || cleaned.length === 0) {
    return 'CNPJ é obrigatório';
  }

  if (cleaned.length !== 14) {
    return 'CNPJ deve ter 14 dígitos';
  }

  if (/^(\d)\1+$/.test(cleaned)) {
    return 'CNPJ inválido';
  }

  if (!validateCNPJ(cnpj)) {
    return 'CNPJ inválido';
  }

  return null;
}
